import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { IconGraphy, Table, Tooltip } from '@leapeasy/ui-kit';
import { Typography, Badge } from '@leapeasy/ui-kit';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Box, Dialog } from '@mui/material';
import { getDemoData } from 'utils/helpers';
import { stageLabelForDisplay } from 'utils/residentTerminology';
import {
  isClaimActionDisabled,
  isCancelPolicyActionDisabled,
  getCancelPolicyDisabledReason,
  resolvePolicyActiveForDisplay,
} from 'utils/policyFlagDisplay';
import { formatBooleanYesNo } from 'utils/formatBooleanYesNo';
import { PolicyCancellationFlow } from 'components/PolicyCancellation';
import { setDocFileAction, setDocTitleAction } from 'store/actions/documentActions';
import { DocuList } from 'components/DocuList';
import { cancelPoliciesAction } from 'store/actions/propertyActions';
import { Layerbar } from 'components/common/Layerbar';
import ClaimForm from 'containers/ClaimForm';
import { ClaimFormErrorBoundary } from 'components/ClaimForm/ClaimFormErrorBoundary';
import { ListExportScopeModal } from '../shared/ListExportScopeModal';
import { TableEmptyState } from 'components/common/TableEmptyState';
import { POLICY_LIST_APP_TYPE_TABLE_META } from 'data/constants/common_constants';
import {
  fitContentCompactColumnOptions,
  fitContentTextColumnOptions,
} from 'utils/fitContentTextColumnOptions';
import {
  POLICIES_TABLE_DEFAULT_VISIBLE_KEYS,
  POLICIES_TABLE_DEFAULT_VISIBLE_KEYS_WITHOUT_BUILDING,
  POLICIES_TABLE_STORAGE_KEY,
} from './policyTableConfig';

const formatTableDate = (value) => {
  if (value == null || value === '') return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
};

export const PolicyTable = forwardRef(
  (
    {
      policies,
      filter,
      dropFilter,
      onChangeFilter,
      pagination,
      onChangePagination,
      sortOptions,
      onChangeSort,
      onClickPolicy,
      shouldShowBuildingName = true,
      clearFlagOnSameCoverageDay: _clearFlagOnSameCoverageDay = false,
      onRequestExportData,
      onRequestFullExportData,
      exportFullDatasetTotal,
      onLoadFullDatasetTotal,
      isLoadingFullDatasetTotal,
      dropFilterActiveCount,
      dropFilterActiveSummary,
      showEmptyState,
      emptyContent,
      loading = false,
    },
    ref,
  ) => {
    const dispatch = useDispatch();
    const [tableData, setTableData] = useState([]);
    const isDemo = useSelector((state) => state.getIn(['ui', 'demo']));
    const successMsg = useSelector((state) => state.getIn(['notification', 'successMsg']));
    const failedMsg = useSelector((state) => state.getIn(['notification', 'failedMsg']));
    const successText = successMsg == null ? '' : String(successMsg);
    const failedText = failedMsg == null ? '' : String(failedMsg);
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const tableRef = useRef(null);
    useImperativeHandle(ref, () => ({
      exportCsv: (...args) => tableRef.current?.exportCsv?.(...args),
    }));

    const buildTableRow = (policy, index) => {
      const buildingName = isDemo ? getDemoData('building-name') : policy.apartment_building_name;
      const policyId = isDemo ? getDemoData('rider-id') : policy.rider_id;
      const tenantName = isDemo ? getDemoData('tenant-name') : policy.tenant_1_name;
      const rent = policy.gross_monthly_rent ? parseFloat(policy.gross_monthly_rent) : 0;
      const issuedDate = formatTableDate(policy.issue_date || policy.sf_createdDate);

      // Schema order matches the `columns` definition below.
      return shouldShowBuildingName
        ? [
            policyId,
            buildingName,
            tenantName,
            policy.stage,
            policy.app_type,
            issuedDate,
            policy.coverage_start_date || '',
            policy.coverage_end_date || '',
            getPolicyActiveValue(policy),
            rent,
            policy.lease_start_date,
            policy.lease_end_date,
            policy.total_number_of_tenants,
            index,
          ]
        : [
            policyId,
            tenantName,
            policy.stage,
            policy.app_type,
            policy.coverage_start_date || '',
            policy.coverage_end_date || '',
            issuedDate,
            getPolicyActiveValue(policy),
            rent,
            policy.lease_start_date,
            policy.lease_end_date,
            policy.total_number_of_tenants,
            index,
          ];
    };

    useEffect(() => {
      const data = [];
      if (policies) {
        policies.forEach((policy, index) => {
          data.push(buildTableRow(policy, index));
        });

        setTableData(data);
      }
    }, [policies, isDemo, shouldShowBuildingName]);

    const goDetailPage = (policy) => {
      if (policy) onClickPolicy(policy);
    };

    const formatCurrency = (value) => {
      const amount = Number(value);
      return Number.isFinite(amount)
        ? `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : '$0.00';
    };

    const getPolicyActiveValue = (policy) => resolvePolicyActiveForDisplay(policy);

    const APP_TYPES = POLICY_LIST_APP_TYPE_TABLE_META;

    const getAppTypeMeta = (value) => {
      if (value == null || value === '') {
        return { label: 'Unknown', badge: 'neutral' };
      }
      return (
        APP_TYPES.find((item) => item.value === value) || { label: String(value), badge: 'neutral' }
      );
    };

    // Row-level actions are now inline; no menu state required.

    const [claimData, setClaimData] = useState(null);
    const [cancelData, setCancelData] = useState(null);
    const [isCancellingPolicy, setIsCancellingPolicy] = useState(false);

    useEffect(() => {
      if (!successText || successText === 'loading') return;
      if (!successText.toLowerCase().includes('cancelled')) return;
      setIsCancellingPolicy(false);
      setCancelData(null);
    }, [successText]);

    useEffect(() => {
      if (!failedText || !isCancellingPolicy) return;
      setIsCancellingPolicy(false);
    }, [failedText, isCancellingPolicy]);

    const handleConfirmCancel = () => {
      if (!cancelData?.rider_id || isCancellingPolicy) return;
      setIsCancellingPolicy(true);
      dispatch(cancelPoliciesAction({ rider_ids: [cancelData.rider_id], page: 'policies' }));
    };

    const handleClaimData = (policy) => {
      if (!policy) return;
      setClaimData(policy);
    };

    const [docuData, setDocuData] = useState(null);

    const handleDownloadInsurance = (policy) => {
      if (!policy) return;
      dispatch(setDocTitleAction(null));
      dispatch(setDocFileAction(null));
      setDocuData(policy);
    };

    const closeDocModal = () => {
      dispatch(setDocTitleAction(null));
      dispatch(setDocFileAction(null));
      setDocuData(null);
    };

    const isCancelRequestInFlight = isCancellingPolicy || successText === 'loading';

    return (
      <React.Fragment>
        <PolicyCancellationFlow
          open={cancelData !== null}
          policy={cancelData}
          onClose={() => {
            if (!isCancelRequestInFlight) setCancelData(null);
          }}
          onConfirmCancel={handleConfirmCancel}
          isSubmitting={isCancelRequestInFlight}
        />
        <Dialog open={!!docuData}>
          <DialogContainer>
            {docuData && (
              <DownloadInsuranceContainer>
                <div className="title">
                  <div>
                    <p>Download Insurance</p>
                    <IconGraphy icon="General.Clear" onClick={closeDocModal} />
                  </div>
                  <p className="desc">Explore and download insurance files.</p>
                </div>
                <DocuList data={docuData.app_id} />
              </DownloadInsuranceContainer>
            )}
          </DialogContainer>
        </Dialog>
        {claimData && (
          <Layerbar>
            <ClaimFormErrorBoundary onClose={() => setClaimData(null)}>
              <ClaimForm data={{ ...claimData }} onClose={() => setClaimData(null)} />
            </ClaimFormErrorBoundary>
          </Layerbar>
        )}
        <Table
          loading={loading}
          showEmptyState={showEmptyState}
          emptyContent={
            emptyContent ?? (
              <TableEmptyState
                entityName="policies"
                hasActiveFilters={Boolean(`${filter?.searchText || ''}`.trim())}
              />
            )
          }
          ref={tableRef}
          onExportButtonClick={() => setExportModalOpen(true)}
          onRequestExportData={async () => {
            const exportRows = onRequestExportData ? await onRequestExportData() : policies;
            return (exportRows || []).map((policy, index) => buildTableRow(policy, index));
          }}
          onRequestFullExportData={
            onRequestFullExportData
              ? async () => {
                  const exportRows = await onRequestFullExportData();
                  return (exportRows || []).map((policy, index) => buildTableRow(policy, index));
                }
              : undefined
          }
          defaultVisibleColumnKeys={
            shouldShowBuildingName
              ? POLICIES_TABLE_DEFAULT_VISIBLE_KEYS
              : POLICIES_TABLE_DEFAULT_VISIBLE_KEYS_WITHOUT_BUILDING
          }
          columnVisibilityStorageKey={POLICIES_TABLE_STORAGE_KEY}
          columnWidthStorageKey={POLICIES_TABLE_STORAGE_KEY}
          columns={[
            {
              name: 'Policy ID',
              options: {
                ...fitContentTextColumnOptions({
                  fitContentCharWidth: 8,
                  maxWidth: 200,
                  flexGrow: 0,
                }),
                sort: true,
                customBodyRenderer: (value) => (
                  <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
                    {value}
                  </CustomTypography>
                ),
              },
              key: 'rider_id',
            },
            ...(shouldShowBuildingName
              ? [
                  {
                    name: 'Property Name',
                    options: {
                      minWidth: 160,
                      flexGrow: 1,
                      ellipsis: false,
                      sort: true,
                      customBodyRenderer: (value) => (
                        <CustomTypography
                          align="left"
                          variant="body2"
                          style={{ cursor: 'pointer' }}
                        >
                          {value}
                        </CustomTypography>
                      ),
                    },
                    key: 'apartment_building_name',
                  },
                ]
              : []),
            {
              name: 'Name',
              options: {
                ...(shouldShowBuildingName
                  ? fitContentTextColumnOptions({
                      fitContentCharWidth: 8,
                      maxWidth: 260,
                      flexGrow: 0,
                    })
                  : { minWidth: 140, flexGrow: 1, ellipsis: false }),
                sort: true,
                customBodyRenderer: (value) => (
                  <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
                    {value}
                  </CustomTypography>
                ),
              },
              key: 'tenant_1_name',
            },
            {
              name: 'Stage',
              options: {
                width: 168,
                minWidth: 168,
                maxWidth: 168,
                shrink: false,
                sort: true,
                customBodyRenderer: (value) => (
                  <Tooltip
                    title={stageLabelForDisplay(value) || 'Unknown'}
                    placement="top"
                    showCarot
                  >
                    <div>
                      <Badge
                        color={value ? 'sunglow' : 'tomato'}
                        label={stageLabelForDisplay(value) || 'Unknown'}
                        rounded
                        textSize="medium"
                      />
                    </div>
                  </Tooltip>
                ),
              },
              key: 'stage',
            },
            {
              name: 'Application Type',
              options: {
                ...fitContentCompactColumnOptions({ minWidth: 130, maxWidth: 190 }),
                sort: true,
                customBodyRenderer: (value) => {
                  const appType = getAppTypeMeta(value);
                  return (
                    <Tooltip title={appType.label} placement="top" showCarot>
                      <div>
                        <Box textAlign="center">
                          <Badge
                            background="#F3F1F4"
                            color={appType.badge}
                            label={appType.label}
                            rounded
                            textSize="medium"
                          />
                        </Box>
                      </div>
                    </Tooltip>
                  );
                },
              },
              key: 'app_type',
            },
            {
              name: 'Issued Date',
              options: {
                width: 110,
                shrink: false,
                sort: true,
                customBodyRenderer: (value) => (
                  <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
                    {value}
                  </CustomTypography>
                ),
              },
              key: 'sf_createdDate',
            },
            {
              name: 'Coverage Start',
              options: {
                width: 120,
                shrink: false,
                sort: true,
                customBodyRenderer: (value) => (
                  <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
                    {value || '-'}
                  </CustomTypography>
                ),
              },
              key: 'coverage_start_date',
            },
            {
              name: 'Coverage End',
              options: {
                width: 120,
                shrink: false,
                sort: true,
                customBodyRenderer: (value) => (
                  <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
                    {value || '-'}
                  </CustomTypography>
                ),
              },
              key: 'coverage_end_date',
            },
            {
              name: 'Active',
              options: {
                width: 90,
                shrink: false,
                sort: true,
                exportValue: (value) => formatBooleanYesNo(value),
                customBodyRenderer: (value) => {
                  const label = formatBooleanYesNo(value);
                  const isActive = value === 'true';
                  return (
                    <Tooltip title={label} placement="top" showCarot>
                      <div>
                        <Badge
                          color={isActive ? 'parisGreen' : 'tomato'}
                          label={label}
                          rounded
                          textSize="medium"
                        />
                      </div>
                    </Tooltip>
                  );
                },
              },
              key: 'active_lease',
            },
            {
              name: 'Monthly Rent',
              options: {
                width: 90,
                shrink: false,
                sort: true,
                exportValue: (value) => formatCurrency(value),
                customBodyRenderer: (value) => (
                  <Typography align="center" variant="body2">
                    ${value.toLocaleString('en-US')}
                  </Typography>
                ),
              },
              key: 'gross_monthly_rent',
            },
            {
              name: 'Lease Start Date',
              options: {
                width: 110,
                shrink: false,
                sort: true,
                customBodyRenderer: (value) => (
                  <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
                    {value}
                  </CustomTypography>
                ),
              },
              key: 'lease_start_date',
            },
            {
              name: 'Lease End Date',
              options: {
                width: 110,
                shrink: false,
                sort: true,
                customBodyRenderer: (value) => (
                  <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
                    {value}
                  </CustomTypography>
                ),
              },
              key: 'lease_end_date',
            },
            {
              name: 'Total Residents',
              options: {
                width: 95,
                shrink: false,
                sort: true,
                customBodyRenderer: (value) => (
                  <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
                    {value}
                  </CustomTypography>
                ),
              },
              key: 'total_number_of_tenants',
            },
            {
              name: 'Actions',
              options: {
                width: 120,
                minWidth: 120,
                maxWidth: 120,
                shrink: false,
                sort: false,
                excludeFromExport: true,
                stickyRight: true,
                customBodyRenderer: (policyIndex) => {
                  const policy = policies?.[policyIndex];
                  const stage = policy?.stage;
                  const actionsAvailable =
                    Boolean(stage) && (stage.includes('02') || stage === '05 - Policy Issued');

                  const LEAP_PURPLE = '#702572';
                  const DISABLED_ICON_COLOR = '#B8B0BC';

                  const detailsDisabled = !policy;
                  const claimDisabled =
                    !actionsAvailable || !policy || isClaimActionDisabled(policy);
                  const downloadDisabled = !actionsAvailable || !policy;
                  const cancelDisabled =
                    !actionsAvailable || !policy || isCancelPolicyActionDisabled(policy);
                  const cancelDisabledReason = getCancelPolicyDisabledReason(policy);

                  const actionTooltip = (title, disabled, disabledReason = null) =>
                    disabled
                      ? disabledReason || `${title} — Not available for this policy stage`
                      : title;

                  return (
                    <ActionsCell>
                      <Tooltip
                        title={actionTooltip('View Policy Details', detailsDisabled)}
                        placement="top"
                        showCarot
                      >
                        <span>
                          <ActionIconButton
                            type="button"
                            disabled={detailsDisabled}
                            onClick={detailsDisabled ? undefined : () => goDetailPage(policy)}
                          >
                            <IconGraphy
                              icon="FileFolder.Description"
                              style={{ color: detailsDisabled ? DISABLED_ICON_COLOR : LEAP_PURPLE }}
                            />
                          </ActionIconButton>
                        </span>
                      </Tooltip>

                      <Tooltip
                        title={actionTooltip('File a claim', claimDisabled)}
                        placement="top"
                        showCarot
                      >
                        <span>
                          <ActionIconButton
                            type="button"
                            disabled={claimDisabled}
                            onClick={claimDisabled ? undefined : () => handleClaimData(policy)}
                          >
                            <IconGraphy
                              icon="FileFolder.EditDocument"
                              style={{ color: claimDisabled ? DISABLED_ICON_COLOR : LEAP_PURPLE }}
                            />
                          </ActionIconButton>
                        </span>
                      </Tooltip>

                      <Tooltip
                        title={actionTooltip('Download Insurance', downloadDisabled)}
                        placement="top"
                        showCarot
                      >
                        <span>
                          <ActionIconButton
                            type="button"
                            disabled={downloadDisabled}
                            onClick={
                              downloadDisabled ? undefined : () => handleDownloadInsurance(policy)
                            }
                          >
                            <IconGraphy
                              icon="FileFolder.SimCardDownload"
                              style={{
                                color: downloadDisabled ? DISABLED_ICON_COLOR : LEAP_PURPLE,
                              }}
                            />
                          </ActionIconButton>
                        </span>
                      </Tooltip>

                      <Tooltip
                        title={actionTooltip('Cancel Policy', cancelDisabled, cancelDisabledReason)}
                        placement="top"
                        showCarot
                      >
                        <span>
                          <ActionIconButton
                            type="button"
                            disabled={cancelDisabled}
                            onClick={cancelDisabled ? undefined : () => setCancelData(policy)}
                          >
                            <IconGraphy
                              icon="General.Clear"
                              style={{ color: cancelDisabled ? DISABLED_ICON_COLOR : LEAP_PURPLE }}
                            />
                          </ActionIconButton>
                        </span>
                      </Tooltip>
                    </ActionsCell>
                  );
                },
              },
              key: 'actions',
            },
          ]}
          data={tableData}
          filter={filter}
          dropFilter={dropFilter}
          dropFilterActiveCount={dropFilterActiveCount}
          dropFilterActiveSummary={dropFilterActiveSummary}
          onChangeFilter={onChangeFilter}
          onChangeRowsPerPage={(value) =>
            onChangePagination({
              ...pagination,
              rowsPerPage: value,
              pageNumber: 0,
            })
          }
          pagination={pagination}
          sortOptions={sortOptions}
          onChangeSort={onChangeSort}
          style={{ width: '100%' }}
          title="Policies"
        />
        <ListExportScopeModal
          open={exportModalOpen}
          onClose={() => setExportModalOpen(false)}
          tableRef={tableRef}
          title="Policies"
          currentPageRowCount={tableData.length}
          paginationTotalItems={pagination.totalItems}
          fullDatasetTotal={exportFullDatasetTotal}
          onLoadFullDatasetTotal={onLoadFullDatasetTotal}
          isLoadingFullDatasetTotal={isLoadingFullDatasetTotal}
          enableFiltered={Boolean(onRequestExportData)}
          enableFull={Boolean(onRequestFullExportData)}
          enableSelected={false}
        />
      </React.Fragment>
    );
  },
);

PolicyTable.displayName = 'PolicyTable';

PolicyTable.defaultProps = {
  showEmptyState: true,
  emptyContent: null,
};

const CustomTypography = styled(Typography)`
  word-break: normal;
  overflow-wrap: break-word;
`;

const DialogContainer = styled.div`
  padding: 20px;
  border-radius: 8px;
`;

const ActionsCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 100%;
`;

const ActionIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  line-height: 0;

  &:disabled {
    cursor: default;
    pointer-events: none;
  }
`;

const DownloadInsuranceContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  * {
    box-sizing: border-box;
  }
  p {
    color: rgba(46, 15, 64, 1);
    margin: 0;
  }
  .title {
    & > div {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px 0;
      font-size: 20px;
      font-weight: 500;
      svg {
        cursor: pointer;
      }
    }
    margin-bottom: 30px;
    border-bottom: 3px solid rgba(46, 15, 64, 0.5);
  }

  & > div:nth-of-type(2) {
    max-height: 373px;
    overflow: auto;
  }

  .desc {
    color: rgba(106, 94, 113, 1);
    font-weight: 400;
    font-size: 12px;
    margin-bottom: 10px;
  }
`;
